import {
  ArrowLeft,
  ArrowRight,
  CheckCircle,
  FileText,
  Scales,
  UserFocus,
} from '@phosphor-icons/react';

import { cn } from '@/lib/utils';
import {
  NiveauBadge,
  descriptionNiveau,
  libelleProgression,
  type HgCategorieReponseQcm,
  type HgNiveauMaitrise,
} from './hg-college-qcm-vue';

/* ── Types (miroir de packages/application/src/trpc/rows/hg-college.ts) ── */

export type HgOrigineNiveau = 'automatique' | 'auto_positionnement';

export interface HgAnalyseDocDocumentRow {
  id: string;
  repere: string;
  titre: string;
  genre: string;
  provenance: string;
  texte: string;
  donnees: { libelle: string; valeur: string }[];
}

export interface HgAnalyseDocItemRow {
  id: string;
  type: 'choix_document' | 'justification';
  consigne: string;
  support_implicite: boolean;
  critere_id: string;
}

export interface HgAnalyseDocExerciceRow {
  id: string;
  fiche_id: string;
  fiche_code: string;
  fiche_titre: string;
  famille: 'analyse_document';
  position: number;
  enonce: string;
  consigne: string | null;
  duree_indicative_s: number | null;
  documents: HgAnalyseDocDocumentRow[];
  items: HgAnalyseDocItemRow[];
}

export interface HgAnalyseDocRetourItemRow {
  item_id: string;
  notion_code: string | null;
  notion: string | null;
  categorie: HgCategorieReponseQcm;
  je_ne_sais_pas: boolean;
  commentaire: string | null;
  explication: string | null;
  document_pertinent_id: string | null;
}

export interface HgAnalyseDocRetourCritereRow {
  critere_id: string;
  code: string;
  libelle: string;
  niveau_atteint: HgNiveauMaitrise;
  origine: HgOrigineNiveau;
  categorie_dominante: HgCategorieReponseQcm | null;
  descripteur: string | null;
  aide_utilisee: boolean;
  n_observations: number;
  dominance_etablie: boolean;
  items: HgAnalyseDocRetourItemRow[];
}

export interface HgAnalyseDocTentativeResultRow {
  tentative_id: string;
  tentative_n: number;
  niveau_global: HgNiveauMaitrise;
  n_criteres_mesures: number;
  aide_utilisee: boolean;
  criteres: HgAnalyseDocRetourCritereRow[];
}

export interface ReponseAnalyseDoc {
  documentId: string | null;
  texte: string | null;
}

/* Descripteurs officiels du format « choix justifié » (§Éval 2), à la première
   personne. Ils sont AFFICHÉS pour que l'élève se situe ; aucune ligne de code
   ne les attribue. */
export const DESCRIPTEURS_JUSTIFICATION_ELEVE: Record<HgNiveauMaitrise, string> = {
  1: "Je n'ai pas justifié mon choix.",
  2: "J'ai justifié, mais mon explication ne tient pas debout.",
  3: "J'ai compris le sens du document et j'ai justifié mon choix de façon cohérente.",
  4: "J'ai tenu compte de la nature du document et de qui l'a écrit, et j'ai expliqué ce qui m'a mis sur la piste.",
};

export const NIVEAUX_AUTO: readonly HgNiveauMaitrise[] = [1, 2, 3, 4];

export interface HgAnalyseDocExerciceVueProps {
  exercice: HgAnalyseDocExerciceRow;
  index: number;
  total: number;
  seriesFaites?: readonly boolean[];
  reponse: ReponseAnalyseDoc;
  autoPositionnement: HgNiveauMaitrise | null;
  result?: HgAnalyseDocTentativeResultRow;
  submitting?: boolean;
  submitError?: string | null;
  onChoisirDocument: (documentId: string) => void;
  onEcrire: (texte: string) => void;
  onAutoPositionner: (niveau: HgNiveauMaitrise) => void;
  onPrecedent: () => void;
  onSuivant: () => void;
  onValider: () => void;
}

export function HgAnalyseDocExerciceVue({
  exercice,
  index,
  total,
  seriesFaites,
  reponse,
  autoPositionnement,
  result,
  submitting,
  submitError,
  onChoisirDocument,
  onEcrire,
  onAutoPositionner,
  onPrecedent,
  onSuivant,
  onValider,
}: HgAnalyseDocExerciceVueProps) {
  const itemChoix = exercice.items.find((item) => item.type === 'choix_document');
  const itemJustification = exercice.items.find((item) => item.type === 'justification');
  const pretAValider =
    reponse.documentId !== null && (reponse.texte ?? '').trim().length > 0 && autoPositionnement !== null;

  return (
    <div>
      <div className="flex items-start gap-[10px] mb-2">
        <FileText size={24} weight="light" className="text-primary shrink-0 mt-[2px]" />
        <h2 className="text-[19px] font-bold text-foreground leading-[1.3]">{exercice.fiche_titre}</h2>
      </div>

      {seriesFaites && seriesFaites.length > 1 && (
        <div className="flex items-center gap-3 mb-2 flex-wrap">
          <div className="flex gap-[5px]" aria-hidden="true">
            {seriesFaites.map((faite, rang) => (
              <span
                key={rang}
                className={cn(
                  'h-[6px] w-9 rounded-full',
                  faite ? 'bg-naia-blue' : rang === index ? 'bg-naia-sky' : 'bg-border',
                )}
              />
            ))}
          </div>
          <span className="text-[14px] text-muted-foreground">{libelleProgression(seriesFaites)}</span>
        </div>
      )}

      <div className="text-[14px] text-muted-foreground mb-6">
        Série {index + 1} / {total} — analyse de document, {exercice.documents.length} documents
      </div>

      <section className="flex flex-col gap-4 mb-7">
        {exercice.documents.map((document) => (
          <DocumentCarte
            key={document.id}
            document={document}
            attendu={result ? documentPertinentDe(result) === document.id : false}
            choisi={reponse.documentId === document.id}
          />
        ))}
      </section>

      {itemChoix && (
        <section className="mb-7">
          <p className="text-[18px] font-bold text-foreground leading-[1.4] mb-3">{itemChoix.consigne}</p>
          <div className="flex flex-col gap-[10px]">
            {exercice.documents.map((document) => {
              const actif = reponse.documentId === document.id;
              return (
                <button
                  key={document.id}
                  type="button"
                  disabled={Boolean(result)}
                  onClick={() => onChoisirDocument(document.id)}
                  className={cn(
                    'flex items-center gap-3 w-full text-left px-4 py-[13px] rounded-card border text-[15px] min-h-[44px] transition-shadow',
                    actif
                      ? 'border-primary bg-primary text-primary-foreground font-bold'
                      : 'border-border bg-card text-foreground hover:shadow-card',
                    result ? 'cursor-default' : 'cursor-pointer',
                  )}
                >
                  <span
                    className={cn(
                      'inline-flex items-center justify-center w-7 h-7 rounded-full border font-bold text-[13px] shrink-0',
                      actif ? 'border-primary-foreground' : 'border-border',
                    )}
                  >
                    {document.repere}
                  </span>
                  {document.titre}
                </button>
              );
            })}
          </div>
        </section>
      )}

      {itemJustification && (
        <section className="mb-7">
          <label
            htmlFor="analysedoc-justification"
            className="block text-[18px] font-bold text-foreground leading-[1.4] mb-3"
          >
            {itemJustification.consigne}
          </label>
          <textarea
            id="analysedoc-justification"
            rows={6}
            maxLength={2000}
            disabled={Boolean(result)}
            value={reponse.texte ?? ''}
            onChange={(event) => onEcrire(event.target.value)}
            className="w-full rounded-card border border-border bg-card p-4 text-[15px] leading-[1.6] text-foreground resize-y disabled:opacity-70"
            placeholder="Écris ta réponse ici."
          />
          <p className="text-[13px] text-muted-foreground mt-2">
            Ce texte n’est pas corrigé par l’application : il est enregistré tel quel et ton
            professeur peut le lire.
          </p>
        </section>
      )}

      {!result && itemJustification && (
        <section className="mb-7 rounded-card border border-border bg-card p-5">
          <p className="flex items-center gap-[10px] m-0 mb-1 text-[16px] font-bold text-foreground">
            <UserFocus size={22} weight="light" className="text-primary shrink-0" />
            Où situes-tu ta justification ?
          </p>
          <p className="m-0 mb-4 text-[14px] text-muted-foreground leading-[1.6]">
            C’est toi qui te situes ici. L’application ne lit pas ton texte.
          </p>
          <div className="flex flex-col gap-[10px]">
            {NIVEAUX_AUTO.map((niveau) => {
              const actif = autoPositionnement === niveau;
              return (
                <button
                  key={niveau}
                  type="button"
                  onClick={() => onAutoPositionner(niveau)}
                  className={cn(
                    'w-full text-left px-4 py-[13px] rounded-card border text-[15px] min-h-[44px] transition-shadow cursor-pointer',
                    actif
                      ? 'border-primary bg-primary text-primary-foreground font-bold'
                      : 'border-border bg-background text-foreground hover:shadow-card',
                  )}
                >
                  {DESCRIPTEURS_JUSTIFICATION_ELEVE[niveau]}
                </button>
              );
            })}
          </div>
        </section>
      )}

      {result && <RetourAnalyseDoc result={result} documents={exercice.documents} />}

      {submitError && <p role="alert" className="text-destructive text-sm mt-4">{submitError}</p>}

      <div className="flex gap-3 mt-7">
        <button
          type="button"
          disabled={index === 0}
          onClick={onPrecedent}
          className="inline-flex items-center gap-2 px-5 py-[11px] rounded-card border border-border bg-card text-foreground font-bold text-[15px] cursor-pointer min-h-[44px] transition-shadow hover:shadow-card disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-none"
        >
          <ArrowLeft size={18} />
          Précédent
        </button>

        {!result && (
          <button
            type="button"
            disabled={!pretAValider || submitting}
            onClick={onValider}
            className="inline-flex items-center gap-2 px-5 py-[11px] rounded-card bg-primary text-primary-foreground font-bold text-[15px] border-none cursor-pointer min-h-[44px] transition-shadow hover:shadow-card disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:shadow-none"
          >
            <CheckCircle size={18} weight="bold" />
            {submitting ? 'Envoi…' : 'Valider'}
          </button>
        )}

        {result && index < total - 1 && (
          <button
            type="button"
            onClick={onSuivant}
            className="inline-flex items-center gap-2 px-5 py-[11px] rounded-card bg-primary text-primary-foreground font-bold text-[15px] border-none cursor-pointer min-h-[44px] transition-shadow hover:shadow-card"
          >
            <ArrowRight size={18} />
            Suivant
          </button>
        )}
      </div>
    </div>
  );
}

function documentPertinentDe(result: HgAnalyseDocTentativeResultRow): string | null {
  for (const critere of result.criteres) {
    for (const item of critere.items) {
      if (item.document_pertinent_id) return item.document_pertinent_id;
    }
  }
  return null;
}

export function DocumentCarte({
  document,
  attendu,
  choisi,
}: {
  document: HgAnalyseDocDocumentRow;
  attendu: boolean;
  choisi: boolean;
}) {
  return (
    <article
      className={cn(
        'rounded-card border bg-card p-5',
        attendu ? 'border-naia-orange-bg' : choisi ? 'border-primary' : 'border-border',
      )}
    >
      {/* Pas de `flex-wrap` ici : un titre long passerait alors SOUS sa
          pastille, et les documents d'un même corpus n'auraient plus la même
          silhouette. Le titre se replie tout seul à l'intérieur de sa colonne. */}
      <div className="flex items-start gap-3 mb-1">
        <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-naia-info-bg text-naia-blue font-bold text-[13px] shrink-0">
          {document.repere}
        </span>
        <h3 className="text-[16px] font-bold text-foreground m-0 leading-[1.35] pt-[3px]">
          {document.titre}
        </h3>
      </div>
      {/* Le genre et la provenance sont AFFICHÉS AVANT le texte : c'est ce que
          la grille officielle demande d'exploiter au niveau 4, et un document
          dont on ignore l'auteur ne peut pas être critiqué. */}
      <p className="text-[13px] text-muted-foreground m-0 mb-3">
        {document.genre} — {document.provenance}
      </p>
      <p className="text-[15px] text-foreground leading-[1.65] m-0 whitespace-pre-line">{document.texte}</p>
      {document.donnees.length > 0 && (
        <div className="overflow-x-auto mt-3">
          <table className="w-full text-[15px] border-collapse">
            <tbody>
              {document.donnees.map((ligne) => (
                <tr key={ligne.libelle} className="border-b border-border last:border-b-0">
                  <th scope="row" className="text-left font-normal text-foreground py-2 pr-4">
                    {ligne.libelle}
                  </th>
                  <td className="text-right font-bold text-foreground py-2 whitespace-nowrap">
                    {ligne.valeur}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </article>
  );
}

export function RetourAnalyseDoc({
  result,
  documents,
}: {
  result: HgAnalyseDocTentativeResultRow;
  documents: HgAnalyseDocDocumentRow[];
}) {
  const repereDe = (id: string | null) =>
    documents.find((document) => document.id === id)?.repere ?? '—';
  return (
    <section className="flex flex-col gap-4">
      {result.criteres.map((critere) => (
        <article key={critere.critere_id} className="rounded-card border border-border bg-card p-5">
          <div className="flex items-center gap-3 mb-2 flex-wrap">
            <NiveauBadge niveau={critere.niveau_atteint} />
            <span className="text-[16px] font-bold text-foreground">{critere.libelle}</span>
          </div>

          {/* Le marquage qui empêche de confondre une mesure et une
              déclaration. Sans lui, les deux cartes se ressemblent, et un
              parent lit « niveau 4 » comme un résultat de l'application. */}
          <p className="flex items-center gap-2 text-[13px] text-muted-foreground m-0 mb-3">
            {critere.origine === 'automatique' ? (
              <>
                <Scales size={16} weight="light" className="shrink-0" />
                Situé par l’application, sur {critere.n_observations} réponse
                {critere.n_observations > 1 ? 's' : ''}.
              </>
            ) : (
              <>
                <UserFocus size={16} weight="light" className="shrink-0" />
                Niveau que tu t’es donné. L’application n’a pas lu ton texte.
              </>
            )}
          </p>

          {critere.origine === 'automatique' && critere.categorie_dominante && (
            <p className="text-[15px] text-foreground leading-[1.6] m-0 mb-2">
              {descriptionNiveau({
                niveau_atteint: critere.niveau_atteint,
                categorie_dominante: critere.categorie_dominante,
                dominance_etablie: critere.dominance_etablie,
              })}
            </p>
          )}

          {critere.descripteur && (
            <p className="text-[15px] text-foreground leading-[1.6] m-0 mb-2">{critere.descripteur}</p>
          )}

          {critere.items.map((item) =>
            item.commentaire || item.explication ? (
              <div key={item.item_id} className="mt-3">
                {item.commentaire && (
                  <p className="text-[15px] text-foreground leading-[1.6] m-0">{item.commentaire}</p>
                )}
                {item.document_pertinent_id && (
                  <p className="text-[15px] text-muted-foreground leading-[1.6] m-0 mt-1">
                    Le document attendu était le {repereDe(item.document_pertinent_id)}.
                  </p>
                )}
                {item.explication && (
                  <p className="text-[15px] text-muted-foreground leading-[1.6] m-0 mt-2">
                    {item.explication}
                  </p>
                )}
              </div>
            ) : null,
          )}
        </article>
      ))}
    </section>
  );
}
